import { createAction } from 'redux-api-middleware';
import { get } from 'lodash';
import { handleError, buildQueryString } from '../utils';

export const SEARCH_REQUEST = 'SEARCH_REQUEST';
export const SEARCH_SUCCESS = 'SEARCH_SUCCESS';
export const SEARCH_UPDATE_SUCCESS = 'SEARCH_UPDATE_SUCCESS';
export const SEARCH_FAILURE = 'SEARCH_FAILURE';
export const SEARCH_PAGE_SIZE = 20;
export const CLEAR_SEARCH_RESULTS = 'CLEAR_SEARCH_RESULTS';

export const CACHED_SEARCH_SUCCESS = 'CACHED_SEARCH_SUCCESS';
export const CACHED_SEARCH_FAILURE = 'CACHED_SEARCH_FAILURE';

const handleSearchResponse = async (action, state, res) => res.json();

/**
 * Fetch home search results based on input value.
 *
 * @param resources
 * @param {string} query - search input query.
 * @return {object} - Redux api middleware action that triggers & handles the request.
 */
export const search = (resources, query) => {
  const resourcesString = resources.map(resource => `&resources[]=${resource}`).join('');

  return createAction({
    endpoint: resources.includes('statements')
      ? `${window.config.sapiUrl}/statements/search?q=${query}`
      : `${window.config.apiUrl}/search?q=${query}${resourcesString}`,
    method: 'GET',
    types: [
      { type: SEARCH_REQUEST },
      {
        type: SEARCH_SUCCESS,
        payload: handleSearchResponse,
        meta: { query, resources },
      },
      { type: SEARCH_FAILURE, payload: handleError },
    ],
  });
};

export const searchByEndpoint = (
  resource,
  { start = 0, count = SEARCH_PAGE_SIZE, ...other } = {},
) => {
  const query = { start, count, ...other };
  return createAction({
    endpoint: `${window.config.apiUrl}/search/${resource}?${buildQueryString(query)}`,
    method: 'GET',
    types: [
      { type: SEARCH_REQUEST },
      {
        type: start === 0 ? SEARCH_SUCCESS : SEARCH_UPDATE_SUCCESS,
        payload: handleSearchResponse,
        meta: (action, state, res) => ({
          query,
          resource,
          totalCount: parseInt(res.headers.get('Total-Count'), 10),
        }),
      },
      { type: SEARCH_FAILURE, payload: handleError },
    ],
  });
};

export const searchByField = (resource, query, { start = 0, count = SEARCH_PAGE_SIZE } = {}) => {
  return createAction({
    endpoint: `${window.config.apiUrl}/search/${resource}?${query}&${buildQueryString({
      start,
      count,
    })}`,
    method: 'GET',
    types: [
      { type: SEARCH_REQUEST },
      {
        type: start === 0 ? SEARCH_SUCCESS : SEARCH_UPDATE_SUCCESS,
        payload: handleSearchResponse,
        meta: (action, state, res) => ({
          query,
          resource,
          totalCount: parseInt(res.headers.get('Total-Count'), 10),
        }),
      },
      { type: SEARCH_FAILURE, payload: handleError },
    ],
  });
};

export const cachedSearchHash = (query, resources) => `${query};${resources.join('&')};`;

/**
 * Fetch home search results based on input value.
 *
 * @param {array} resources
 * @param {string} query - search input query.
 * @param {object} options
 * @return {object} - Redux api middleware action that triggers & handles the request.
 */
export const cachedSearch = (resources, query, options = { useCache: true }) => {
  const resourcesString = resources.map(resource => `&resources[]=${resource}`).join('');
  const hash = cachedSearchHash(query, resources);

  return createAction({
    endpoint: resources.includes('statements')
      ? `${window.config.sapiUrl}/statements/search?q=${query}`
      : `${window.config.apiUrl}/search?q=${query}${resourcesString}`,
    bailout: store => options.useCache && get(store, `cachedSearch.${hash}`, false),
    method: 'GET',
    types: [
      { type: SEARCH_REQUEST },
      {
        type: CACHED_SEARCH_SUCCESS,
        payload: handleSearchResponse,
        meta: { query, resources, hash },
      },
      { type: SEARCH_FAILURE, payload: handleError },
    ],
  });
};

export const clearSearchResults = () => {
  return {
    type: CLEAR_SEARCH_RESULTS,
  };
};
